import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import { getProjectTheme } from './helpers/getProjectTheme';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { MainState } from './models/main-state';
import { initialMainState } from './assets/config/initial-main-state';
import { MainContext } from './store/MainContext';
import { BrowserRouter, useLocation } from 'react-router-dom';
import Views from './routes/Views';
import { getUserLanguageInCookie, setUserLanguageInCookie } from './helpers/cookiesHandler';
import { polishPhrases } from './languages/polishPhrases';
import { frenchPhrases } from './languages/frenchPhrases';
import { arabicPhrases } from './languages/arabicPhrases';
import { englishPhrases } from './languages/englishPhrases';
import { initialCountriesState } from './assets/config/initial-countries-state';
import { CountriesState } from './models/countries-state';
import { CountriesContext } from './store/CountriesContext';
import { DirectionContext } from './store/DirectionContext';
import { initialDirection } from 'assets/config/initial-direction-state';
import { MemoizedAxiosWrapper } from './assets/api/axiosInterceptor';
import { Configuration, ConfigurationContext, initialConfiguration } from 'store/configurationContext';
import axios from 'axios';
import { API_ENDPOINTS } from 'assets/api/endpoints';
import { NotificationTypeContext } from 'store/NotificationTypeContext';
import { HolidayProvider } from 'store/HolidayContext';
import { AutoApprovalContext } from 'store/AutoApprovalContext';
import { CaptchaConfig } from 'store/CaptchaConfiguration';

function App() {
	const [mainState, setMainState] = React.useState<MainState>(initialMainState);
	const [countriesState, setCountriesState] = React.useState<CountriesState>(initialCountriesState);
	const [directionState, setDirectionState] = React.useState<'ltr' | 'rtl' | undefined>(initialDirection);
	const [configurationState, setConfigurationState] = React.useState<Configuration[]>(initialConfiguration);
	const [notificationType, setNotificationType] = React.useState<string>('');
	const [isAutoApproval, setAutoApproval] = React.useState<boolean>(false);
	const SUPPORTED_LANGS = ['en', 'ar', 'fr', 'pl'];
	const getInitialLanguage = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const urlLang = urlParams.get('lang');
		if (urlLang && SUPPORTED_LANGS.includes(urlLang)) {
			const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
			localStorage.setItem('userInfo', JSON.stringify({ ...userInfo, language: urlLang }));
			return urlLang;
		}
		const userInfoLang = JSON.parse(localStorage.getItem('userInfo') || '{}')?.language;
		if (userInfoLang && SUPPORTED_LANGS.includes(userInfoLang)) return userInfoLang;
		return getUserLanguageInCookie();
	};
	i18n.use(initReactI18next).init({
		resources: {
			en: { translation: englishPhrases },
			ar: { translation: arabicPhrases },
			fr: { translation: frenchPhrases },
			pl: { translation: polishPhrases },
		},
		lng: getInitialLanguage(),
		fallbackLng: ['en', 'ar', 'pl', 'fr'].includes(navigator.language) ? navigator.language : 'en',
		interpolation: { escapeValue: false },
	});

	useEffect(() => {
		axios
			.post(API_ENDPOINTS.getGeneralConfig, {
				keys: [
					'Company_Name',
					'tab_logo',
					'header_image',
					'company_logo',
					'theme_color',
					'company_website',
					'session_expired_url',
					'background_image',
					'full_logo',
					'recaptchaVersion',
					'isRecaptchaEnabled',
					'withdraw_required_live_picture',
					'default_language',
					'secondary_color',
					'createAccountLink',
					'becomeMerchantLink',
					'termsAndConditionsLink',
					'hide_header_text',
					'disable_timer',
					'enable_deposit_operation_otp',
					'enable_withdraw_operation_otp',
					'auto_approval',
					'restrict_deposit_orders',
					'bonus_groups',
				],
			})
			.then((response) => {
				const data = response.data.filter((item: Configuration) => item.configKey === 'secondary_color');
				if (data.length > 0) {
					setConfigurationState(response.data);
				} else {
					setConfigurationState([
						...response.data,
						{
							configId: 13,
							configKey: 'secondary_color',
							configValue: 'white',
							isPublic: true,
							value: 'white',
						},
					]);
				}
			})
			.catch((err) => console.error(err));
	}, []);

	return (
		<ConfigProvider theme={getProjectTheme()} direction={directionState}>
			<CountriesContext.Provider value={{ countriesState, setCountriesState }}>
				<MainContext.Provider value={{ mainState, setMainState }}>
					<DirectionContext.Provider value={{ directionState, setDirectionState }}>
						<ConfigurationContext.Provider value={{ configurationState, setConfigurationState }}>
							<NotificationTypeContext.Provider value={{ notificationType, setNotificationType }}>
								<AutoApprovalContext.Provider value={{ isAutoApproval, setAutoApproval }}>
									<BrowserRouter>
										<MemoizedAxiosWrapper>
											<HolidayProvider>
												<CaptchaConfig>
													<Views />
												</CaptchaConfig>
											</HolidayProvider>
										</MemoizedAxiosWrapper>
									</BrowserRouter>
								</AutoApprovalContext.Provider>
							</NotificationTypeContext.Provider>
						</ConfigurationContext.Provider>
					</DirectionContext.Provider>
				</MainContext.Provider>
			</CountriesContext.Provider>
		</ConfigProvider>
	);
}

export default App;
